import { Injectable } from '@angular/core';
import { inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, from, map, of, switchMap, tap } from 'rxjs';
import { ErrorHandlingService } from './error-handling.service';
import { environment } from 'src/environments/environment';
import { jwtDecode } from 'jwt-decode';
import { jwtVerify, createRemoteJWKSet, JWTPayload } from 'jose';
import { CustomJwtPayload } from 'src/app/interfaces/custom-jwt-payload';
import { getLocalStorageRoleNames } from 'src/app/utils/local-storage.util';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  backendUrl: string = environment.backendUrl;
  router = inject(Router);
  private document = inject(DOCUMENT);

  // BehaviorSubject to hold user roles
  private userRolesSubject = new BehaviorSubject<string[]>(
    getLocalStorageRoleNames()
  );
  userRoles$ = this.userRolesSubject.asObservable();
  private jwksCache = new Map<string, ReturnType<typeof createRemoteJWKSet>>();

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getAuthMetadata(): Observable<any> {
    return this.http.get<any>(`${this.backendUrl}/user/getAuthMetadata`).pipe(
      map((result) => result),
      tap((res) => {
        const outcome = res ? 'fetched' : 'did not find';
        console.log(`getAuthMetadata: ${outcome}`);
      }),
      catchError(this.errorHandlingService.handleError<any>(`getAuthMetadata`))
    );
  }

  getTeamRelationshipsForCurrentUser(): Observable<any> {
    return this.http
      .get<any>(`${this.backendUrl}/user/getTeamRelationshipsForCurrentUser`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getTeamRelationshipsForCurrentUser: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getTeamRelationshipsForCurrentUser`
          )
        )
      );
  }

  getAuthMetadataForAllAccounts(): Observable<any> {
    return this.http
      .get<any>(`${this.backendUrl}/user/getAuthMetadataForAllAccounts`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getAuthMetadataForAllAccounts: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getAuthMetadataForAllAccounts`
          )
        )
      );
  }

  isSsoUser(): boolean {
    const isSso = localStorage.getItem('is_sso_user');
    return isSso === 'true';
  }

  getAdminPortalUrl(): string {
    const userUuid = localStorage.getItem('user_uuid');
    const isSso = this.isSsoUser();
    return `${environment.adminPortal}/?user_uuid=${userUuid}${isSso ? '&is_sso=true' : ''}`;
  }

  logoutRequest = () => {
    return this.http.get<any>(`${this.backendUrl}/logout`).pipe(
      tap((res) => {
        const outcome = res ? 'fetched' : 'fetched empty'; // This will return fetched empty for this case
        console.log(`Logged out successfully: ${outcome}`);
        const logoutUrl = res?.logoutUrl;
        if (logoutUrl && !this.isSsoUser()) {
          console.log(`Logout successful, redirecting to Cognito logout URL`);
          try {
            const parsed = new URL(logoutUrl);
            if (parsed.protocol === 'https:') {
              this.document.location.href = logoutUrl;
            } else {
              console.error('Logout URL rejected: non-HTTPS protocol');
            }
          } catch {
            console.error('Logout URL rejected: invalid URL');
          }
        } else {
          console.log('Navigating to /logout route');
          this.router.navigate(['/logout']);
        }
        localStorage.clear();
      }),
      catchError(this.errorHandlingService.handleError<any>(`Logout`))
    );
  };

  // Update user roles in localStorage and notify subscribers of userRoles$
  updateUserRoles(newRoles: string[]): void {
    localStorage.setItem('roles', JSON.stringify(newRoles));
    this.userRolesSubject.next(newRoles); // Emitting new roles to subscribers
  }
}
